import { CellPosition } from "@app/grid/GridCell";
import { newRowWrites } from "@app/grid/newRowWrites";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FieldType, TYPE_ITEMS_RELATION_KEY, columnAggregation } from "@dto/fields";
import ObjectActionDto from "@dto/ObjectAction";
import { ObjectInfos } from "@dto/objects";
import { Period } from "@dto/period";
import { DeleteRecordRequest, Direction, DuplicateRecordRequest, FieldColumn, Filter, ImportCsvRequest, Item, ItemKey, ItemValue, Line, MetaLine, PatchItemRequest, Path, PathsRequest, PathsResponse, Record, RecordLinksRequest, Relation, UpdateRecordRequest, getOppositeDirection , PageRequest } from "@dto/records";
import { ObjectRelation } from "@dto/relation";
import { Group, Pivot, Sorting, Summary, View } from "@dto/views";
import { RowSelectionState } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { BASE_URL, SHOW_ALL_LINES } from "./constants";
import { fillErroredColumnFields } from "./columnErrors";
import { fieldToPath, getFieldByPathKeys, getHiddenPathKeys, getPathKeys } from "./model";
import { isSummable } from "./summaries";
import { getUndoSessionId } from "./session";
import { UndoLabelCode, useUndoStore } from "./undo";
import { toastError, toastSuccess } from "./toasts";
import { del, post, put } from "./utils";

export const updateItem = async (itemKeys: ItemKey[], value: any) => {

    const workspace = useModelStore.getState().selectedWorkspace;
    if (!workspace) {
        throw new Error("Workspace not selected");
    }
    const activeObject = useModelStore.getState().selectedObjectInfos?.object._key || itemKeys[0]?.objectKey;
    const request: UpdateRecordRequest = {
        workspaceKey: workspace._key,
        ItemValues: itemKeys.map(key => ({ key: key, value: value })),
        Replace: true,
        sessionId: getUndoSessionId(),
        objectKey: activeObject,
    }

    const response = await newRowWrites.write(itemKeys.map(key => key.recordKey), put(BASE_URL + '/data/records', request));
    if (!isCompozzError(response) && activeObject) {
        useUndoStore.getState().markUndoable(activeObject, UndoLabelCode.update, itemKeys.length);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}

// updateItemValues writes many (cell, value) pairs in a SINGLE atomic request, so
// a multi-cell paste/fill is one backend mutation and one undoable operation
// (not one per cell). The grid is refreshed optimistically by the caller.
export const updateItemValues = async (itemValues: ItemValue[], objectKey?: string) => {

    const workspace = useModelStore.getState().selectedWorkspace;
    if (!workspace) {
        throw new Error("Workspace not selected");
    }
    if (itemValues.length === 0) {
        return null;
    }
    const activeObject = objectKey || useModelStore.getState().selectedObjectInfos?.object._key || itemValues[0]?.key.objectKey;
    const request: UpdateRecordRequest = {
        workspaceKey: workspace._key,
        ItemValues: itemValues,
        Replace: true,
        sessionId: getUndoSessionId(),
        objectKey: activeObject,
    }

    const response = await newRowWrites.write(itemValues.map(itemValue => itemValue.key.recordKey), put(BASE_URL + '/data/records', request));
    if (!isCompozzError(response) && activeObject) {
        useUndoStore.getState().markUndoable(activeObject, UndoLabelCode.update, itemValues.length);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}

export const patchItems = async (patchRequest: PatchItemRequest) => {

    const workspace = useModelStore.getState().selectedWorkspace;
    if (!workspace) {
        throw new Error("Workspace not selected");
    }

    const objKey = patchRequest.objectKey;
    const recordKey = patchRequest.recordKey;
    const itemValues: ItemValue[] = [];
    patchRequest.valuesByFieldKeys.forEach((value, fieldKey) => {
        itemValues.push({ key: { recordKey: recordKey, objectKey: objKey, fieldKey: fieldKey }, value: value });
    });

    const activeObject = useModelStore.getState().selectedObjectInfos?.object._key || objKey;
    const request: UpdateRecordRequest = {
        workspaceKey: workspace._key,
        ItemValues: itemValues,
        Replace: false,
        sessionId: getUndoSessionId(),
        objectKey: activeObject,
    }

    const response = await newRowWrites.write([recordKey], put(BASE_URL + '/data/records', request));
    if (!isCompozzError(response) && activeObject) {
        useUndoStore.getState().markUndoable(activeObject, UndoLabelCode.update, itemValues.length);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}


export const getPathByFields = async (
    workspaceKey: string,
    objectKey: string,
    fields: Field[],
    relationByKeys: Map<string, ObjectRelation> | null,
    fieldByKeys: Map<string, Field> | null,
    recordKeys: string[] | null = null,
    filter: Filter | null = null,
    sortings: Sorting[] | null = null,
    groups: Group[] | null = null,
    pivot: Pivot | null = null,
    period: Period | null = null,
    aggregations: Map<string, AggregationType> | null = null,
    fieldOrder: string[] | null = null,
    limit: number | null = null,
    signal?: AbortSignal,
    page: PageRequest | null = null,
    summaries: Summary[] | null = null,
    summariesOnly: boolean = false
): Promise<PathsResponse | CompozzError | null> => {

    const paths = fields.map((field) => {
        return fieldToPath(objectKey, relationByKeys, fieldByKeys, field);
    }); 

    if (paths.some(path => path === null)) {
        const fieldColumns = fields.map((field) => {
            return {
                pathKey: null,
                field: field,
                srcField: null,
                isPivot: false,
                pivotValue: null,
                dependencies: null
            };
        });
        return { lines: [], columns: fieldColumns, pivot: null };
    }

    const nonNullPaths = paths.filter(path => path !== null) as Path[];
    const response = await getPaths(workspaceKey, objectKey, nonNullPaths, recordKeys, filter, sortings, groups, pivot, period, aggregations, fieldOrder, limit, signal, page, summaries, summariesOnly);
    if (response && !isCompozzError(response) && response.columns) {
        return { ...response, columns: fillErroredColumnFields(response.columns, fields) };
    }
    return response;
}

export const getPaths = async (
    workspaceKey: string,
    objectKey: string,
    paths: Path[],
    recordKeys: string[] | null,
    filter: Filter | null = null,
    sortings: Sorting[] | null = null,
    groups: Group[] | null = null,
    pivot: Pivot | null = null,
    period: Period | null = null,
    aggregations: Map<string, AggregationType> | null = null,
    fieldOrder: string[] | null = null,
    limit: number | null = null,
    signal?: AbortSignal,
    page: PageRequest | null = null,
    summaries: Summary[] | null = null,
    summariesOnly: boolean = false
): Promise<PathsResponse | CompozzError | null> => {

    if (paths.length === 0) {
        return { lines: [], columns: [], pivot: null };
    }

    const request: PathsRequest = {
        workspaceKey: workspaceKey,
        objectKey: objectKey,
        paths: paths,
        recordKeys: recordKeys,
        filter: filter,
        sortings: sortings,
        groups: groups,
        pivot: pivot,
        period: period,
        aggregations: aggregations,
        summaries: summaries,
        summariesOnly: summariesOnly || undefined,
        fieldOrder: fieldOrder,
        limit: limit,
        page: page
    }
    return post<PathsResponse>(`${BASE_URL}/data/records/paths`, request, 0, undefined, signal);
}

export const exportPaths = async (
    workspaceKey: string,
    objectKey: string,
    paths: Path[],
    filter: Filter | null = null,
    sortings: Sorting[] | null = null,
    groups: Group[] | null = null,
    pivot: Pivot | null = null,
    period: Period | null = null,
    aggregations: Map<string, AggregationType> | null = null,
    fieldOrder: string[] | null = null,
    limit: number | null = null
): Promise<string | CompozzError | null> => {

    if (paths.length === 0) {
        return "";
    }

    const request: PathsRequest = {
        workspaceKey: workspaceKey,
        objectKey: objectKey,
        paths: paths,
        recordKeys: null,
        filter: filter,
        sortings: sortings,
        groups: groups,
        pivot: pivot,
        period: period,
        aggregations: aggregations,
        summaries: null,
        fieldOrder: fieldOrder,
        limit: limit
    }
    return post<string>(`${BASE_URL}/data/records/csv/export`, request, 0, new Headers({ 'Accept': 'text/csv;base64' }));
}

// Message shown when a CSV import fails: the oversized-payload and timeout
// cases get an actionable wording, everything else follows the usual
// CompozzError idiom (translated i18n code, else the raw message).
export function csvImportErrorMessage(error: CompozzError, t: TFunction<"translation", undefined>): string {
    if (error.code === 413) {
        return t("record.csv.import.error.too.large");
    }
    if (error.code === 408 || error.code === 504) {
        return t("record.csv.import.error.timeout");
    }
    if (error.i18n) {
        return translate(t, error.i18n, error.params);
    }
    return error.message || t("record.csv.import.error");
}

// Imports CSV rows into an object using the compact form (column names once,
// one value array per record). Every failure (backend error, oversized
// payload, timeout, network failure) raises its own toast; the caller only
// reacts to the boolean.
export const importCsvLines = async (
    workspaceKey: string,
    objectKey: string,
    columns: string[],
    rows: any[][],
    t: TFunction<"translation", undefined>
): Promise<boolean> => {
    const request: ImportCsvRequest = { workspaceKey, objectKey, columns, rows };
    let response: Record[] | CompozzError | null;
    try {
        response = await post<Record[]>(`${BASE_URL}/data/records/csv/import`, request);
    } catch (error) {
        // The HTTP layer converts failures into CompozzError values; this guard
        // only protects against an unexpected rejection.
        console.error("CSV import failed:", error);
        toastError(t("record.csv.import.error"));
        return false;
    }
    if (isCompozzError(response)) {
        console.error("CSV import refused:", response);
        toastError(csvImportErrorMessage(response, t));
        return false;
    }
    // 200 with the created records, or an empty response: both mean success
    return true;
}

export const getRecords = async (workspaceKey: string, objectKey: string, recordKeys: string[] | null, fieldKeys: string[], filter: Filter | null): Promise<Record[]> => {

    const relationByKeys = useModelStore.getState().relationByKeys;
    const fieldByKeys = useModelStore.getState().fieldByKeys ?? new Map();
    const fields = fieldKeys.map(fieldKey => fieldByKeys.get(fieldKey)).filter(field => field !== undefined) as Field[];
    const response = await getPathByFields(workspaceKey, objectKey, fields, relationByKeys, fieldByKeys, recordKeys, filter, null, null, null, null, null, null, null);
    if (!response) {
        console.error("No response from getPaths");
        return [];
    }

    if (isCompozzError(response)) {
        console.error(response);
        return [];
    }
    const lines = response.lines;
    const records = lines.map((line, index) => {
        // The item of a link field hangs on the record (its parent), its key is the linked record
        const firstItem = line.items[0];
        return {
            _key: firstItem.parentKey?.recordKey && firstItem.parentKey.objectKey === objectKey ? firstItem.parentKey.recordKey : firstItem.key.recordKey,
            order: index,
            fields: new Map(line.items.map(item => [item.key.fieldKey, item.value]))
        };
    });
    return records;
}


export const getLink = async (workspaceKey: string, originObjKey: string, relationKey: string, srcRecordKey: string, direction: Direction) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: srcRecordKey,
        destRecordKeys: null,
        direction: direction,
        linkKeys: null
    }
    return post<Relation[]>(`${BASE_URL}/data/links/search`, request);
}

export const getLinks = async (workspaceKey: string, originObjKey: string, relationKey: string, srcRecordKeys: string[], direction: Direction) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: null,
        destRecordKeys: srcRecordKeys,
        direction: getOppositeDirection(direction),
        linkKeys: null
    }
    return post<Relation[]>(`${BASE_URL}/data/links/search`, request);
}

export const deleteLinks = async (workspaceKey: string, originObjKey: string, originRecordKey: string, relationKey: string, direction: Direction, linkKeys: string[]) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: originRecordKey,
        destRecordKeys: null,
        direction: direction,
        linkKeys: linkKeys,
        sessionId: getUndoSessionId(),
    }
    const response = await newRowWrites.write([originRecordKey], del<void>(`${BASE_URL}/data/records/link`, request));
    if (!isCompozzError(response)) {
        useUndoStore.getState().markUndoable(originObjKey, UndoLabelCode.unlink);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}

export const addLink = async (workspaceKey: string, originObjKey: string, relationKey: string, srcRecordKey: string, destRecordKey: string, direction: Direction) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: srcRecordKey,
        destRecordKeys: [destRecordKey],
        direction: direction,
        linkKeys: null,
        sessionId: getUndoSessionId(),
    }
    const response = await newRowWrites.write([srcRecordKey], post<Relation[]>(`${BASE_URL}/data/records/link`, request));
    if (!isCompozzError(response)) {
        useUndoStore.getState().markUndoable(originObjKey, UndoLabelCode.link);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}


export const addLinks = async (workspaceKey: string, originObjKey: string, relationKey: string, srcRecordKey: string, destRecordKeys: string[], direction: Direction) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: srcRecordKey,
        destRecordKeys: destRecordKeys,
        direction: direction,
        linkKeys: null,
        sessionId: getUndoSessionId(),
    }
    const response = await newRowWrites.write([srcRecordKey], post<Relation[]>(`${BASE_URL}/data/records/link`, request));
    if (!isCompozzError(response)) {
        useUndoStore.getState().markUndoable(originObjKey, UndoLabelCode.link);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}

export const addLinksFromSeveralSources = async (workspaceKey: string, originObjKey: string, relationKey: string, srcRecordKeys: string[], destRecordKey: string, direction: Direction) => {

    const request: RecordLinksRequest = {
        workspaceKey: workspaceKey,
        originObjKey: originObjKey,
        relationKey: relationKey,
        srcRecordKey: destRecordKey,
        destRecordKeys: srcRecordKeys,
        direction: getOppositeDirection(direction),
        linkKeys: null,
        sessionId: getUndoSessionId(),
    }
    const response = await newRowWrites.write(srcRecordKeys, post<Relation[]>(`${BASE_URL}/data/records/link`, request));
    if (!isCompozzError(response)) {
        useUndoStore.getState().markUndoable(originObjKey, UndoLabelCode.link);
        useDataStore.getState().refreshSummaries();
    }
    return response;
}

// Deletes records as an undoable operation of the session. No response means success.
export const deleteRecordsUndoable = async (workspaceKey: string, objectKey: string, recordKeys: string[]) => {
    const request: DeleteRecordRequest = {
        workspaceKey: workspaceKey,
        objectKey: objectKey,
        recordKeys: recordKeys,
        sessionId: getUndoSessionId(),
    }
    const response = await del<void>(`${BASE_URL}/data/records`, request);
    if (!response) {
        useUndoStore.getState().markUndoable(objectKey, UndoLabelCode.delete, recordKeys.length);
    }
    return response;
}

export const shouldClearData = (view: View | null) => {
    if (view?.shownPaths) {
        return view?.groups?.some(group => !view!.shownPaths!.includes(group.pathKey));
    }

    return view?.groups?.some(group => view?.hiddenPaths?.includes(group.pathKey) === true);
}


// Ceiling on the number of rows to fetch: the "Afficher toutes les lignes"
// toggle applies to the default grid only — a view always shows its full
// result set. null means no ceiling.
const resolveRowLimit = (view: View | null, objectKey: string): number | null => {
    if (view !== null) {
        return null;
    }
    const showAllLines = localStorage.getItem(SHOW_ALL_LINES + objectKey);
    return showAllLines === null || showAllLines === "true" ? null : 10;
}

export const fetchView = async (view: View | null, period: Period | null, recordKeys: string[] | null = null, signal?: AbortSignal, page: PageRequest | null = null, summariesOnly: boolean = false) => {
    const objectInfos = useModelStore.getState().selectedObjectInfos;
    const workspace = useModelStore.getState().selectedWorkspace;
    const relationByKeys = useModelStore.getState().relationByKeys;
    const fieldByKeys = useModelStore.getState().fieldByKeys;
    if (!objectInfos || !workspace) {
        return { lines: [], columns: [] };
    }
    const pivotPathKey = view?.pivot?.pivotPathKey;
    const pivotValuePathKeys = view?.pivot?.valuePathKeys;
    const fieldByPathKeys = getFieldByPathKeys(objectInfos, objectInfos?.fields || [], relationByKeys, fieldByKeys);
    const pathKeys = getPathKeys(objectInfos, objectInfos?.fields || [], relationByKeys, fieldByKeys, true).filter(pathKey => pathKey !== null) as string[];
    const hiddenPathKeys = getHiddenPathKeys(view, pathKeys);
    const hiddenFields = hiddenPathKeys.filter(pathKey => pivotPathKey !== pathKey && !pivotValuePathKeys?.includes(pathKey))?.map(pathKey => fieldByPathKeys.get(pathKey)) || [];
    const hiddenFieldsKeys = hiddenFields.map(field => field?._key);

    let filter: Filter | null = null;
    if (view && view.filter) {
        filter = view.filter;
        if (!filter.groupFilter) {
            filter = null;
        } else {
            filter.groupFilter.filters = filter.groupFilter.filters?.filter(f => f.groupFilter?.filters?.length && f.groupFilter.filters[0].singleFilter && fieldByPathKeys.get(f.groupFilter.filters[0].singleFilter?.pathKey));
        }
    }

    let sortings: Sorting[] | null = null;
    if (view && view.sortings) {
        sortings = view.sortings.filter(sorting => fieldByPathKeys.get(sorting.pathKey));
    }

    let groups: Group[] | null = null;
    if (view && view.groups) {
        groups = view.groups.filter(group => fieldByPathKeys.get(group.pathKey));
    }

    let pivot: Pivot | null = null;
    if (view && view.pivot && view.pivot.pivotPathKey) {
        pivot = view.pivot;
        const pivotField = fieldByPathKeys?.get(view.pivot.pivotPathKey);
        if (!pivotField) {
            console.error("Pivot field not found");
            return { lines: [], columns: [] };
        }
        if ((pivotField.type === FieldType.Date || pivotField.type === FieldType.Period) && !period) {
            return { lines: [], columns: [], pivot: pivotField };
        }
    }

    let aggregations: Map<string, AggregationType> | null = null;
    if (view && view.aggregations) {
        aggregations = view.aggregations;
    }

    let summaries: Summary[] | null = null;
    if (view && view.summaries) {
        // Summability follows the column aggregation (a counted text column
        // displays numbers), resolved like the grid does
        summaries = view.summaries.filter(summary => {
            const field = fieldByPathKeys.get(summary.pathKey);
            return isSummable(field, columnAggregation(field, view.aggregations, summary.pathKey));
        });
    }

    let fieldOrder: string[] | null = null;
    if (view && view.fieldOrder) {
        fieldOrder = view.fieldOrder.filter(fieldKey => fieldByPathKeys.get(fieldKey));
    }

    const limit = resolveRowLimit(view, objectInfos.object._key);
    if (!summariesOnly) {
        // Publish the ceiling so the grid can flag the truncation and refuse record
        // creation without re-reading localStorage at render time — the toggle then
        // reaches the grid immediately instead of waiting for a remount.
        useDataStore.setState({ appliedLimit: limit });

        if (shouldClearData(view)) {
            clearData();
        }
    }

    const fields = (objectInfos?.fields || []).filter(field => !hiddenFieldsKeys.includes(field._key));
    const response = await getPathByFields(workspace._key, objectInfos.object._key, fields, relationByKeys, fieldByKeys, recordKeys, filter, sortings, groups, pivot, period, aggregations, fieldOrder, limit, signal, page, summaries, summariesOnly);
    if (!response) {
        console.error("No response from getPaths");
        return { lines: [], columns: [] };
    }

    if (isCompozzError(response)) {
        console.error(response);
        return { lines: [], columns: [] };
    }
    return response;
}

export function clearData(clearUserRole: boolean = false) {
    const updateData = useDataStore.getState().updateData;
    updateData([], []);
    if (clearUserRole) {
        const updateUserRole = useModelStore.getState().updateUserRole;
        updateUserRole(null);
    }
}

export const getRecordKeys = function (metaLine: MetaLine, objKey: string, onlyOne: boolean = false): string[] {
    const recordKeys: string[] = [];
    for (const item of metaLine.line.items) {
        const parentRecordKey = item.parentKey?.recordKey;
        const parentObjectKey = item.parentKey?.objectKey;
        if (parentRecordKey !== undefined && parentRecordKey !== null && parentRecordKey !== "") {
            if (!recordKeys.includes(parentRecordKey) && parentObjectKey === objKey) {
                if (onlyOne) {
                    return [parentRecordKey];
                }
                recordKeys.push(parentRecordKey);
            }
            continue;
        }
        const recordKey = item.key.recordKey;
        const objectKey = item.key.objectKey;
        if (recordKey !== undefined && recordKey !== null && recordKey !== "") {
            if (!recordKeys.includes(recordKey) && objectKey === objKey) {
                if (onlyOne) {
                    return [recordKey];
                }
                recordKeys.push(recordKey);
            }
            continue;
        }
    };
    return recordKeys;
}

// Returns the keys of the records of the given object shown on the selected
// rows, each once (a record may be shown on several rows)
export const getSelectedRecordKeys = function (rowSelection: RowSelectionState, metaLines: Map<string, MetaLine>, objKey: string): string[] {
    const recordKeys = new Set<string>();
    Object.keys(rowSelection).filter((rowKey) => rowSelection[rowKey]).forEach((rowKey) => {
        const metaLine = metaLines.get(rowKey);
        if (metaLine) {
            getRecordKeys(metaLine, objKey).forEach((recordKey) => recordKeys.add(recordKey));
        }
    });
    return Array.from(recordKeys);
}

// Tells whether the action can be executed on the given records: without
// record, only an action always visible can (it is then sent without line)
export const canExecuteObjectAction = (action: ObjectActionDto, recordKeys: string[]): boolean =>
    recordKeys.length > 0 || !!action.alwaysVisible;

// Executes an object action on the given records and toasts its outcome;
// onSuccess is called once the action succeeded. Nothing is sent when the
// action cannot be executed on these records (see canExecuteObjectAction).
export const executeObjectAction = async (workspaceKey: string, objectKey: string, action: ObjectActionDto, recordKeys: string[], onSuccess: () => void) => {
    if (!canExecuteObjectAction(action, recordKeys)) {
        return;
    }
    try {
        const result = await post(`${BASE_URL}/data/action/execute`, {
            actionKey: action._key,
            recordKeys,
            workspaceKey,
            objectKey,
            type: "object",
        });
        if (isCompozzError(result)) {
            toastError(result.message || action.errorMsg);
        } else {
            toastSuccess((result as any)?.message ?? action.successMsg);
            onSuccess();
        }
    } catch {
        toastError(action.errorMsg);
    }
}

export async function fillPivot(
    wsKey: string,
    position: CellPosition | null,
    fieldColumn: FieldColumn,
    view: View,
    item: Item,
    value: any,
    metaLines: Map<string, MetaLine>,
    fieldByPathKeys: Map<string, Field>,
    selectedObjectInfos: ObjectInfos,
    t: TFunction,
    updateState: (item: Item) => void,
) {


    const pivotPathKey = view?.pivot?.pivotPathKey;
    const pivotValue = fieldColumn.pivotValue;
    if (position && fieldColumn.isPivot && pivotPathKey && pivotValue) {
        const pivotField = fieldByPathKeys.get(pivotPathKey);
        if (!pivotField) {
            return;
        }
        const isPivotLink = pivotField.type === FieldType.Link;
        const isValueLink = (fieldColumn.srcField ?? fieldColumn.field).type === FieldType.Link;
        const metaline = metaLines.get(position.rowId);
        if (!metaline) {
            console.error("No metaline found for row " + position.rowId);
            return;
        }
        const lineRecordKeys = getRecordKeys(metaline, selectedObjectInfos.object._key, true);
        if (lineRecordKeys.length === 0) {
            console.error("No record keys found for row " + position.rowId);
            return;
        }

        let rowKeyToclone = lineRecordKeys[0];

        const overridenFieldsByRecordKey = new Map();
        const overridenLinksByRecordKey = new Map();
        const overridenFields = new Map();
        if (!isPivotLink) {
            overridenFields.set(pivotField._key, pivotValue.value);
        } else {
            const overridenLinks = overridenLinksByRecordKey.get(rowKeyToclone) || new Map();
            const relationKey = pivotField.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
            if (relationKey) {
                overridenLinks.set(relationKey, [pivotValue.value.key.recordKey]);
            }
            overridenLinksByRecordKey.set(rowKeyToclone, overridenLinks);
        }
        if (!isValueLink) {
            overridenFields.set(fieldColumn.field._key, value);
        } else {
            const overridenLinks = overridenLinksByRecordKey.get(rowKeyToclone) || new Map();
            const relationKey = (fieldColumn.srcField ?? fieldColumn.field).typeItems?.get(TYPE_ITEMS_RELATION_KEY);
            if (relationKey) {
                overridenLinks.set(relationKey, [value.key.recordKey]);
            }
            overridenLinksByRecordKey.set(rowKeyToclone, overridenLinks);
        }
        overridenFieldsByRecordKey.set(rowKeyToclone, overridenFields);
        if (rowKeyToclone) {
            const request: DuplicateRecordRequest = {
                workspaceKey: wsKey,
                objectKey: selectedObjectInfos.object._key,
                recordKeys: [rowKeyToclone],
                overridenFields: overridenFieldsByRecordKey,
                overridenLinks: overridenLinksByRecordKey,
                sessionId: getUndoSessionId(),
            }
            newRowWrites.write([rowKeyToclone], post(`${BASE_URL}/data/records/duplicate`, request)).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else if (Array.isArray(response) && response.length > 0) {
                        const record = response[0];
                        useUndoStore.getState().markUndoable(selectedObjectInfos.object._key, UndoLabelCode.duplicate, 1);
                        useDataStore.getState().refreshSummaries();
                        const rowItemKey = { recordKey: record._key, objectKey: selectedObjectInfos.object._key, fieldKey: (fieldColumn.srcField ?? fieldColumn.field)._key };
                        if (isValueLink) {
                            updateState({ ...item, value: value.value, key: value.key, parentKey: rowItemKey });
                        } else {
                            updateState({ ...item, value: value, key: rowItemKey });
                        }
                    } else {
                        toastError(t("record.duplicate.error"));
                    }
                } else {
                    toastError(t("record.duplicate.error"));
                }
            })
        }
    }
}


export const exportView = async (view: View | null, period: Period | null): Promise<string | CompozzError | null> => {
    const objectInfos = useModelStore.getState().selectedObjectInfos;
    const workspace = useModelStore.getState().selectedWorkspace;
    const relationByKeys = useModelStore.getState().relationByKeys;
    const fieldByKeys = useModelStore.getState().fieldByKeys;
    if (!objectInfos || !workspace) {
        return "";
    }
    const pivotPathKey = view?.pivot?.pivotPathKey;
    const pivotValuePathKeys = view?.pivot?.valuePathKeys;
    const fieldByPathKeys = getFieldByPathKeys(objectInfos, objectInfos?.fields || [], relationByKeys, fieldByKeys);

    const pathKeys = getPathKeys(objectInfos, objectInfos?.fields || [], relationByKeys, fieldByKeys, true).filter(pathKey => pathKey !== null) as string[];
    const hiddenPathKeys = getHiddenPathKeys(view, pathKeys);
    const hiddenFields = hiddenPathKeys.filter(pathKey => pivotPathKey !== pathKey && !pivotValuePathKeys?.includes(pathKey))?.map(pathKey => fieldByPathKeys.get(pathKey)) || [];
    const hiddenFieldsKeys = hiddenFields.map(field => field?._key);

    let filter: Filter | null = null;
    if (view && view.filter) {
        filter = view.filter;
        if (!filter.groupFilter) {
            filter = null;
        } else {
            filter.groupFilter.filters = filter.groupFilter.filters?.filter(f => f.groupFilter?.filters?.length && f.groupFilter.filters[0].singleFilter && fieldByPathKeys.get(f.groupFilter.filters[0].singleFilter?.pathKey));
        }
    }

    let sortings: Sorting[] | null = null;
    if (view && view.sortings) {
        sortings = view.sortings.filter(sorting => fieldByPathKeys.get(sorting.pathKey));
    }

    let groups: Group[] | null = null;
    if (view && view.groups) {
        groups = view.groups.filter(group => fieldByPathKeys.get(group.pathKey));
    }

    let pivot: Pivot | null = null;
    if (view && view.pivot && view.pivot.pivotPathKey) {
        pivot = view.pivot;
        const pivotField = fieldByPathKeys?.get(view.pivot.pivotPathKey);
        if (!pivotField) {
            console.error("Pivot field not found");
            return "";
        }
        if ((pivotField.type === FieldType.Date || pivotField.type === FieldType.Period) && !period) {
            return "";
        }
    }

    let aggregations: Map<string, AggregationType> | null = null;
    if (view && view.aggregations) {
        aggregations = view.aggregations;
    }

    let fieldOrder: string[] | null = null;
    if (view && view.fieldOrder) {
        fieldOrder = view.fieldOrder.filter(fieldKey => fieldByPathKeys.get(fieldKey));
    }

    const limit = resolveRowLimit(view, objectInfos.object._key);

    if (shouldClearData(view)) {
        clearData();
    }

    const fields = (objectInfos?.fields || []).filter(field => !hiddenFieldsKeys.includes(field._key));

    const paths = fields.map((field) => {
        return fieldToPath(objectInfos.object._key, relationByKeys, fieldByKeys, field);
    }); 

    if (paths.some(path => path === null)) {
        return "";
    }

    const nonNullPaths = paths.filter(path => path !== null) as Path[];

    const response = await exportPaths(workspace._key, objectInfos.object._key, nonNullPaths, filter, sortings, groups, pivot, period, aggregations, fieldOrder, limit);
    if (!response) {
        console.error("No response from exportPaths");
        return "";
    }
    return response;
}

// Helper function to extract all rowIds from Line[] structure in table order
export function getAllRowIdsFromData(lines: Line[]): string[] {
    const rowIds: string[] = [];
    const collectRowIds = (lines: Line[]) => {
        lines.forEach(line => {
            if (line._rowId && !rowIds.includes(line._rowId)) {
                rowIds.push(line._rowId);
            }
            if (line.children && line.children.length > 0) {
                collectRowIds(line.children);
            }
        });
    };
    collectRowIds(lines);
    return rowIds;
}